import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getUpdatePhysicalSocketDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-physical-socket.form-default-values';
import { UpdatePhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-physical-socket.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoUpdatePhysicalSocketMutation } from '@service/cpms-bo-charge-point.service';

import {
  UpdateChargePointChannelPhysicalSocketContextProvider,
  useChargePointChannelContext,
  useChargePointChannelPhysicalSocketContext,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelPhysicalSocketUpdateFormController } from './charge-point-channel-physical-socket-update-form/charge-point-channel-physical-socket-update-form';

interface ChargePointChannelPhysicalSocketUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelPhysicalSocketUpdateController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelPhysicalSocketUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updatePhysicalSocketModel] = useCpmsBoUpdatePhysicalSocketMutation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const physicalSocket = useChargePointChannelPhysicalSocketContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<
      UpdatePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocketId' | 'physicalSocket'
    >
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-physical-socket-update.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<
      UpdatePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocketId' | 'physicalSocket'
    >
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.physicalSocket) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    updatePhysicalSocketModel({
      chargePointId: fieldValues.chargePointId,
      channelId: fieldValues.channelId,
      physicalSocketId: fieldValues.physicalSocketId,
      physicalSocket: sanitizedObject.physicalSocket,
    })
      .unwrap()
      .then(() => {
        toast.success(
          t('$.charge-point-channel-physical-socket-update.success'),
        );
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-physical-socket-update.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getUpdatePhysicalSocketDefaultValues(
    chargePoint.id,
    channel.id,
    physicalSocket,
  );

  return (
    <UpdateChargePointChannelPhysicalSocketContextProvider data={data}>
      <ChargePointChannelPhysicalSocketUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateChargePointChannelPhysicalSocketContextProvider>
  );
};
